import { useMemo, useState } from 'react';
import { failedOnramps, ApiClientError, type FailedOnramp } from '../lib/api';
import { useResource } from '../hooks/useResource';
import { formatDateTime } from '../lib/format';
import { Alert, Badge, Card, Copyable, Empty, Spinner } from '../components/ui';

const ADDR = 'https://basescan.org/address/';
const short = (h: string) => `${h.slice(0, 8)}…${h.slice(-6)}`;

/**
 * Onramps where the customer's NGN landed but the USDC never delivered —
 * usually the wallet was short of liquidity. Top up the wallet, then click
 * Fulfil: it re-sends the EXACT locked amount. One transfer per order is
 * enforced server-side, so it can't double-send, and Fulfil is blocked until
 * the wallet actually covers the order.
 */
export function FailedOnramps() {
  const res = useResource(() => failedOnramps.list(), [], { refreshMs: 30000 });
  const [working, setWorking] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [q, setQ] = useState('');

  const walletUsdc = res.data?.[0]?.wallet_usdc ?? null;

  async function fulfill(orderId: string, usdc: string | null) {
    if (!confirm(`Fulfil this onramp? This sends ${usdc ?? ''} USDC to the customer now.`)) return;
    setError(null);
    setOk(null);
    setWorking(orderId);
    try {
      const r = await failedOnramps.fulfill(orderId);
      setOk(
        r.fulfilled
          ? `Delivered ${r.amount_usdc} USDC${r.tx_hash ? ` (tx ${short(r.tx_hash)})` : ''}`
          : `Delivery ${r.status} — will finalize shortly`
      );
      await res.reload();
    } catch (caught) {
      setError(caught instanceof ApiClientError ? caught.message : 'Fulfilment failed');
    } finally {
      setWorking(null);
    }
  }

  const rows = useMemo(() => {
    const all = res.data ?? [];
    const term = q.trim().toLowerCase();
    if (!term) return all;
    const hit = (o: FailedOnramp) =>
      [o.integrator_ref, o.destination_address, o.order_id, o.amount_ngn, o.amount_usdc, o.error]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(term));
    return all.filter(hit);
  }, [res.data, q]);

  return (
    <>
      <div className="page-header">
        <h1>Failed onramps</h1>
        <p>
          Onramps where the NGN was collected but the USDC never delivered — usually low wallet
          liquidity. Top up the wallet, then fulfil each one; it re-sends the exact locked amount
          and can&rsquo;t double-pay.
        </p>
      </div>

      {error && <Alert>{error}</Alert>}
      {ok && <Alert tone="success">{ok}</Alert>}

      <Card
        title={`Awaiting fulfilment${rows.length ? ` (${rows.length})` : ''}`}
        actions={walletUsdc != null ? <span className="muted">Wallet: {walletUsdc} USDC</span> : undefined}
      >
        <div style={{ marginBottom: 12, maxWidth: 360 }}>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search ref, address, amount, error…"
            style={{ width: '100%' }}
          />
        </div>

        {res.loading && !res.data ? (
          <Spinner />
        ) : !rows.length ? (
          <Empty>{q ? 'No matches.' : 'No failed onramps — all delivered. ✓'}</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Destination</th>
                  <th>Paid (NGN)</th>
                  <th>Owed (USDC)</th>
                  <th>Rate</th>
                  <th>Liquidity</th>
                  <th>Attempts</th>
                  <th>Last error</th>
                  <th>Order</th>
                  <th>When</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.order_id}>
                    <td>
                      <Copyable
                        value={r.destination_address}
                        display={short(r.destination_address)}
                        href={ADDR + r.destination_address}
                      />
                    </td>
                    <td className="mono">{r.amount_ngn ? `₦${r.amount_ngn}` : '—'}</td>
                    <td className="mono">{r.amount_usdc ?? '—'}</td>
                    <td className="mono">{r.rate ?? '—'}</td>
                    <td>
                      <Badge tone={r.has_liquidity ? 'success' : 'warning'}>
                        {r.has_liquidity ? 'covered' : 'top up'}
                      </Badge>
                    </td>
                    <td className="mono">{r.delivery_attempts}</td>
                    <td className="muted" style={{ maxWidth: 260 }}>
                      {r.error ?? '—'}
                    </td>
                    <td>
                      <Copyable value={r.order_id} display={r.order_id.slice(0, 8)} />
                    </td>
                    <td className="muted">{formatDateTime(r.created_at)}</td>
                    <td>
                      <button
                        className="btn btn-primary btn-sm"
                        disabled={working === r.order_id || !r.has_liquidity}
                        title={r.has_liquidity ? undefined : 'Wallet does not cover this order — top up first'}
                        onClick={() => fulfill(r.order_id, r.amount_usdc)}
                      >
                        {working === r.order_id ? 'Sending…' : 'Fulfil'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="muted" style={{ fontSize: 12.5, marginTop: 10 }}>
          Fulfil re-sends the exact USDC locked at order time — never a requote. One delivery per
          order is enforced, so it can&rsquo;t double-send. Rows disappear once delivered.
        </p>
      </Card>
    </>
  );
}
